import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
} from '@mui/material';
import { DialogTitleWithClose } from '../../common';
import {
  CheckCircle as SuccessIcon,
  PlayArrow as ExecutionIcon,
  AccountTree as WorkflowIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import { apiFetch } from '../../../config/api';
import { formatDateTime } from '../../../utils/dateUtils';
import { createIOSButtonStyles } from '../../../theme/iosButtonStyles';
import { useTheme } from '@mui/material/styles';
import { debugError } from '../../../utils/debugLog';

// Helper functions
const getStageStatusColor = status => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    case 'pending':
      return 'grey';
    default:
      return 'primary';
  }
};

const getStageIcon = stageId => {
  switch (stageId) {
    case 'preflight':
      return <SuccessIcon />;
    case 'execution':
      return <ExecutionIcon />;
    case 'postflight':
      return <SuccessIcon />;
    default:
      return <WorkflowIcon />;
  }
};

const formatDuration = seconds => {
  if (!seconds) return 'N/A';
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  return `${seconds.toFixed(2)}s`;
};

const WorkflowDetailsDialog = ({ open, onClose, executionId, execution }) => {
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);

  const [workflowDetails, setWorkflowDetails] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);
  const [experimentDetailsExpanded, setExperimentDetailsExpanded] =
    useState(false);

  useEffect(() => {
    if (open && executionId) {
      setWorkflowLoading(true);
      setWorkflowError(null);
      setWorkflowDetails(null);
      setExperimentDetailsExpanded(false); // Reset expanded state for new execution

      apiFetch(`/api/v1/templates/executions/${executionId}/workflow`)
        .then(data => {
          setWorkflowDetails(data);
        })
        .catch(err => {
          let errorMessage = 'Failed to load workflow details.';
          if (err?.message) {
            errorMessage = err.message;
          } else if (err?.response?.data?.detail) {
            errorMessage = err.response.data.detail;
          }
          setWorkflowError(errorMessage);
          debugError('Error fetching workflow details:', {
            error: err,
            executionId: executionId,
          });
        })
        .finally(() => {
          setWorkflowLoading(false);
        });
    }
  }, [open, executionId]);

  const handleClose = () => {
    setWorkflowDetails(null);
    setWorkflowError(null);
    setExperimentDetailsExpanded(false); // Reset expanded state on close
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitleWithClose
        title={
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkflowIcon color="primary" />
              <Typography variant="h6">Workflow Execution Details</Typography>
            </Box>
            {execution && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {execution.id}
              </Typography>
            )}
          </Box>
        }
        onClose={handleClose}
        disableClose={workflowLoading}
      />
      <DialogContent>
        {workflowLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LinearProgress sx={{ width: '100%' }} />
          </Box>
        )}
        {workflowError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {workflowError}
          </Alert>
        )}
        {workflowDetails && !workflowLoading && (
          <Box>
            {/* Summary Information */}
            {(() => {
              // Extract correct template version from stage events if available
              let correctTemplateVersion = workflowDetails.template_version;
              if (
                workflowDetails.stage_events &&
                workflowDetails.stage_events.length > 0
              ) {
                // Try to get version from preflight stage metadata or output
                const preflightEvent = workflowDetails.stage_events.find(
                  e => e.stage_id === 'preflight'
                );
                if (preflightEvent) {
                  const versionFromMetadata =
                    preflightEvent.metadata?.template_version;
                  const versionFromOutput =
                    preflightEvent.output?.template_version ||
                    preflightEvent.output?.preflight_result?.template
                      ?.version_number;
                  correctTemplateVersion =
                    versionFromMetadata ||
                    versionFromOutput ||
                    correctTemplateVersion;
                }
              }

              return (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Overall Status
                    </Typography>
                    <Chip
                      label={workflowDetails.overall_status || 'Unknown'}
                      color={
                        workflowDetails.overall_status === 'completed'
                          ? 'success'
                          : workflowDetails.overall_status === 'failed'
                            ? 'error'
                            : 'default'
                      }
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Stage
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {workflowDetails.current_stage || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Template
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {workflowDetails.template_id}
                      </Typography>
                      {correctTemplateVersion && (
                        <Chip
                          label={`v${correctTemplateVersion}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {workflowDetails.created_at
                        ? formatDateTime(workflowDetails.created_at)
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })()}

            {/* Timeline */}
            <Typography
              variant="h6"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <WorkflowIcon />
              Execution Timeline
            </Typography>
            {workflowDetails.stage_events &&
            workflowDetails.stage_events.length > 0 ? (
              <Timeline position="right" sx={{ p: 0 }}>
                {workflowDetails.stage_events.map((event, index) => (
                  <TimelineItem key={event.event_id || index}>
                    <TimelineOppositeContent
                      sx={{ flex: 0.3, px: 2 }}
                      color="text.secondary"
                    >
                      <Typography variant="caption">
                        {event.started_at
                          ? formatDateTime(event.started_at)
                          : 'N/A'}
                      </Typography>
                      {event.execution_time && (
                        <Typography variant="caption" display="block">
                          {formatDuration(event.execution_time)}
                        </Typography>
                      )}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineConnector />
                      <TimelineDot
                        color={getStageStatusColor(event.status)}
                        variant={
                          event.status === 'completed' ? 'filled' : 'outlined'
                        }
                      >
                        {React.cloneElement(getStageIcon(event.stage_id), {
                          sx: { fontSize: '1rem' },
                        })}
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: '12px', px: 2 }}>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        fontWeight="bold"
                      >
                        {event.stage_name || event.stage_id}
                      </Typography>
                      {event.processor && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {event.processor}
                        </Typography>
                      )}
                      <Chip
                        label={event.status}
                        size="small"
                        color={getStageStatusColor(event.status)}
                        sx={{ mt: 0.5, mb: 1 }}
                      />

                      {/* Extract and display stage-specific information */}
                      {(() => {
                        const output = event.output || {};
                        const metadata = event.metadata || {};
                        const details = [];

                        // Model and provider information (for preflight and execution)
                        let modelInfo = null;
                        let providerInfo = null;

                        if (event.stage_id === 'preflight') {
                          // Preflight stage: get from preflight_result or adapter_config
                          const preflightResult =
                            output.preflight_result || output;
                          const adapterConfig =
                            preflightResult?.adapter_config ||
                            preflightResult?.model_config ||
                            {};
                          modelInfo =
                            adapterConfig?.model_name ||
                            adapterConfig?.model ||
                            metadata?.model;
                          providerInfo =
                            adapterConfig?.provider ||
                            preflightResult?.provider;

                          // Add model and provider info
                          if (providerInfo) {
                            details.push({
                              label: 'Provider',
                              value: providerInfo,
                              chip: true,
                              color: 'default',
                            });
                          }
                          if (modelInfo) {
                            details.push({
                              label: 'Model',
                              value: modelInfo,
                              chip: true,
                              color: 'primary',
                            });
                          }

                          // Experiment info from preflight
                          if (
                            output.experiment_info ||
                            metadata.experiment_id ||
                            metadata.is_experiment
                          ) {
                            const expInfo = output.experiment_info || {};
                            const experimentId =
                              expInfo.experiment_id || metadata.experiment_id;
                            const treatmentName =
                              expInfo.treatment_name || metadata.treatment_name;
                            const treatmentId =
                              expInfo.treatment_id || metadata.treatment_id;
                            const trafficAllocation =
                              expInfo.traffic_allocation ||
                              expInfo.traffic_allocation_method ||
                              metadata.traffic_allocation_method;

                            // Add experiment indicator (always visible)
                            if (experimentId) {
                              details.push({
                                label: 'Experiment',
                                value: experimentId,
                                chip: true,
                                color: 'secondary',
                                alwaysVisible: true,
                              });
                            } else if (metadata.is_experiment) {
                              details.push({
                                label: 'Experiment',
                                value: 'Yes',
                                chip: true,
                                color: 'secondary',
                                alwaysVisible: true,
                              });
                            }

                            // Add treatment name if available (expandable)
                            if (treatmentName) {
                              details.push({
                                label: 'Treatment',
                                value: treatmentName,
                                chip: true,
                                color: 'default',
                                expandable: true,
                              });
                            }

                            // Add treatment ID if available (and different from name) (expandable)
                            if (treatmentId && treatmentId !== treatmentName) {
                              details.push({
                                label: 'Treatment ID',
                                value: treatmentId,
                                monospace: true,
                                expandable: true,
                              });
                            }

                            // Add traffic allocation method if available (expandable)
                            if (trafficAllocation) {
                              details.push({
                                label: 'Traffic Allocation',
                                value: trafficAllocation,
                                expandable: true,
                              });
                            }
                          }

                          // Template info
                          if (output.template_name || metadata.template_name) {
                            details.push({
                              label: 'Template',
                              value:
                                output.template_name || metadata.template_name,
                            });
                          }
                          if (
                            output.template_version ||
                            metadata.template_version
                          ) {
                            details.push({
                              label: 'Version',
                              value: `v${output.template_version || metadata.template_version}`,
                              chip: true,
                              color: 'primary',
                            });
                          }

                          // Content check info
                          if (preflightResult?.has_violations !== undefined) {
                            details.push({
                              label: 'Content Check',
                              value: preflightResult.has_violations
                                ? 'Violations Found'
                                : 'No Violations',
                              chip: true,
                              color: preflightResult.has_violations
                                ? 'error'
                                : 'success',
                            });
                          }

                          // Estimated time
                          if (
                            output.estimated_time ||
                            event.estimated_execution_time
                          ) {
                            details.push({
                              label: 'Estimated Time',
                              value: formatDuration(
                                output.estimated_time ||
                                  event.estimated_execution_time
                              ),
                            });
                          }
                        } else if (event.stage_id === 'execution') {
                          // Execution stage: get from execution_result
                          const execResult = output.execution_result || {};
                          modelInfo =
                            execResult?.model_used || output?.model_used;
                          providerInfo =
                            execResult?.provider_used || output?.provider_used;

                          // Add model and provider info
                          if (providerInfo) {
                            details.push({
                              label: 'Provider',
                              value: providerInfo,
                              chip: true,
                              color: 'default',
                            });
                          }
                          if (modelInfo) {
                            details.push({
                              label: 'Model',
                              value: modelInfo,
                              chip: true,
                              color: 'primary',
                            });
                          }

                          // Token usage
                          const tokenUsage =
                            execResult?.token_usage || output?.token_usage;
                          if (tokenUsage) {
                            details.push({
                              label: 'Tokens',
                              value: `${tokenUsage.total_tokens || 0} (${tokenUsage.prompt_tokens || 0} + ${tokenUsage.completion_tokens || 0})`,
                            });
                          }

                          // Cost
                          if (
                            execResult?.cost !== null &&
                            execResult?.cost !== undefined
                          ) {
                            details.push({
                              label: 'Cost',
                              value: `$${execResult.cost.toFixed(4)}`,
                            });
                          }
                        } else if (event.stage_id === 'postflight') {
                          // Postflight stage: get from execution object
                          const execution = output.execution || {};

                          // Format information (MOST IMPORTANT for postflight)
                          if (execution?.output_format) {
                            details.push({
                              label: 'Output Format',
                              value: execution.output_format,
                              chip: true,
                              color: 'primary',
                              priority: 1, // High priority
                            });
                          }

                          // Structured output status
                          if (
                            execution?.structured_output !== null &&
                            execution?.structured_output !== undefined
                          ) {
                            // Check if structured_output has content
                            // For strings: check if non-empty
                            // For objects/arrays: check if has keys/elements
                            const hasStructuredOutput =
                              typeof execution.structured_output === 'string'
                                ? execution.structured_output.trim().length > 0
                                : execution.structured_output &&
                                  (Array.isArray(execution.structured_output)
                                    ? execution.structured_output.length > 0
                                    : Object.keys(execution.structured_output)
                                        .length > 0);
                            details.push({
                              label: 'Structured Output',
                              value: hasStructuredOutput ? 'Available' : 'None',
                              chip: true,
                              color: hasStructuredOutput
                                ? 'success'
                                : 'default',
                              priority: 1, // High priority
                            });
                          }

                          // Parsing success/failure
                          if (execution?.parsing_success !== undefined) {
                            details.push({
                              label: 'Parsing',
                              value: execution.parsing_success
                                ? 'Success'
                                : 'Failed',
                              chip: true,
                              color: execution.parsing_success
                                ? 'success'
                                : 'error',
                              priority: 1, // High priority
                            });
                          }

                          // Parsing error if any
                          if (execution?.parsing_error) {
                            details.push({
                              label: 'Parsing Error',
                              value: execution.parsing_error,
                              error: true,
                            });
                          }

                          // Validation errors
                          if (
                            execution?.validation_errors &&
                            execution.validation_errors.length > 0
                          ) {
                            details.push({
                              label: 'Validation Errors',
                              value: `${execution.validation_errors.length} error(s)`,
                              chip: true,
                              color: 'error',
                            });
                          }

                          // Token usage
                          const tokenUsage =
                            execution?.token_usage || output?.token_usage;
                          if (tokenUsage) {
                            details.push({
                              label: 'Tokens',
                              value: `${tokenUsage.total_tokens || 0} (${tokenUsage.prompt_tokens || 0} + ${tokenUsage.completion_tokens || 0})`,
                            });
                          }

                          // Cost
                          if (
                            execution?.cost !== null &&
                            execution?.cost !== undefined
                          ) {
                            details.push({
                              label: 'Cost',
                              value: `$${execution.cost.toFixed(4)}`,
                            });
                          }

                          // Quality score
                          if (
                            execution?.quality_score !== undefined &&
                            execution?.quality_score !== null
                          ) {
                            details.push({
                              label: 'Quality Score',
                              value: execution.quality_score.toString(),
                            });
                          }
                        }

                        // Sort details by priority (priority 1 items first)
                        const sortedDetails = [...details].sort((a, b) => {
                          const priorityA = a.priority || 0;
                          const priorityB = b.priority || 0;
                          return priorityB - priorityA;
                        });

                        // Separate always visible and expandable details
                        const alwaysVisibleDetails = sortedDetails.filter(
                          d => d.alwaysVisible || !d.expandable
                        );
                        const expandableDetails = sortedDetails.filter(
                          d => d.expandable
                        );

                        // Check if there are expandable experiment details
                        const hasExpandableExperimentDetails =
                          expandableDetails.length > 0 &&
                          sortedDetails.some(d => d.label === 'Experiment');

                        // Find the index of Experiment in alwaysVisibleDetails
                        const experimentIndex = alwaysVisibleDetails.findIndex(
                          d => d.label === 'Experiment'
                        );

                        // Helper function to render a detail row
                        const renderDetailRow = (
                          detail,
                          showExpandButton = false
                        ) => (
                          <Box
                            key={`${detail.label}-${detail.value}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                              position: 'relative',
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                minWidth: '100px',
                                fontWeight: detail.priority === 1 ? 600 : 500,
                                color:
                                  detail.priority === 1
                                    ? 'primary.main'
                                    : 'text.secondary',
                              }}
                            >
                              {detail.label}:
                            </Typography>
                            {detail.error ? (
                              <Alert
                                severity="error"
                                sx={{
                                  py: 0,
                                  px: 1,
                                  fontSize: '0.7rem',
                                  flex: 1,
                                }}
                              >
                                {detail.value}
                              </Alert>
                            ) : detail.chip ? (
                              <Chip
                                label={detail.value}
                                size="small"
                                color={detail.color || 'default'}
                                variant={
                                  detail.priority === 1 ? 'filled' : 'outlined'
                                }
                                sx={{
                                  fontWeight: detail.priority === 1 ? 600 : 400,
                                }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: detail.monospace
                                    ? 'monospace'
                                    : 'inherit',
                                  fontSize: detail.monospace
                                    ? '0.7rem'
                                    : '0.75rem',
                                  color: 'text.primary',
                                  fontWeight: detail.priority === 1 ? 600 : 400,
                                }}
                              >
                                {detail.value}
                              </Typography>
                            )}
                            {showExpandButton &&
                              hasExpandableExperimentDetails && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExperimentDetailsExpanded(
                                      !experimentDetailsExpanded
                                    )
                                  }
                                  sx={{
                                    p: 0.25,
                                    ml: 1,
                                  }}
                                >
                                  {experimentDetailsExpanded ? (
                                    <RemoveIcon fontSize="small" />
                                  ) : (
                                    <AddIcon fontSize="small" />
                                  )}
                                </IconButton>
                              )}
                          </Box>
                        );

                        return sortedDetails.length > 0 ? (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              color="text.secondary"
                              sx={{ mb: 1, display: 'block' }}
                            >
                              Stage Details
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                              }}
                            >
                              {/* Render details before Experiment */}
                              {experimentIndex > 0 &&
                                alwaysVisibleDetails
                                  .slice(0, experimentIndex)
                                  .map(detail => renderDetailRow(detail))}

                              {/* Render Experiment with expand button */}
                              {experimentIndex >= 0 &&
                                renderDetailRow(
                                  alwaysVisibleDetails[experimentIndex],
                                  true
                                )}

                              {/* Render expandable details immediately after Experiment */}
                              {experimentDetailsExpanded &&
                                expandableDetails.map(detail => (
                                  <Box
                                    key={`${detail.label}-${detail.value}`}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      flexWrap: 'wrap',
                                      pl: 3, // Indent to show hierarchy
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        minWidth: '100px',
                                        fontWeight:
                                          detail.priority === 1 ? 600 : 500,
                                        color:
                                          detail.priority === 1
                                            ? 'primary.main'
                                            : 'text.secondary',
                                      }}
                                    >
                                      {detail.label}:
                                    </Typography>
                                    {detail.error ? (
                                      <Alert
                                        severity="error"
                                        sx={{
                                          py: 0,
                                          px: 1,
                                          fontSize: '0.7rem',
                                          flex: 1,
                                        }}
                                      >
                                        {detail.value}
                                      </Alert>
                                    ) : detail.chip ? (
                                      <Chip
                                        label={detail.value}
                                        size="small"
                                        color={detail.color || 'default'}
                                        variant={
                                          detail.priority === 1
                                            ? 'filled'
                                            : 'outlined'
                                        }
                                        sx={{
                                          fontWeight:
                                            detail.priority === 1 ? 600 : 400,
                                        }}
                                      />
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontFamily: detail.monospace
                                            ? 'monospace'
                                            : 'inherit',
                                          fontSize: detail.monospace
                                            ? '0.7rem'
                                            : '0.75rem',
                                          color: 'text.primary',
                                          fontWeight:
                                            detail.priority === 1 ? 600 : 400,
                                        }}
                                      >
                                        {detail.value}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}

                              {/* Render remaining details after Experiment */}
                              {experimentIndex >= 0 &&
                                experimentIndex <
                                  alwaysVisibleDetails.length - 1 &&
                                alwaysVisibleDetails
                                  .slice(experimentIndex + 1)
                                  .map(detail => renderDetailRow(detail))}

                              {/* If no Experiment found, render all details normally */}
                              {experimentIndex < 0 &&
                                alwaysVisibleDetails.map(detail =>
                                  renderDetailRow(detail)
                                )}
                            </Box>
                          </Box>
                        ) : null;
                      })()}

                      {event.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {event.error}
                        </Alert>
                      )}
                      {event.execution_time && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Duration: {formatDuration(event.execution_time)}
                        </Typography>
                      )}
                      {event.completed_at && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          Completed: {formatDateTime(event.completed_at)}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            ) : (
              <Alert severity="info">
                No workflow stage events found for this execution.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            ...iosButtonStyles.secondary,
            textTransform: 'none',
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowDetailsDialog;
